/**
 * Portal Authentication Context
 *
 * Story: 11-1-portal-de-acesso-externo
 * Task: Frontend - PortalAuthContext
 *
 * SEPARATE authentication context for external partner portal
 * CRITICAL: Completely isolated from internal system AuthContext
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { PortalUserInfo, PortalLoginRequest } from "@/types/portal";
import { portalAuthApi, portalStorage, getErrorMessage } from "@/services/portalApi";
import { marcarDonoDoPush } from "@/lib/push/donoDoPush";

// ============================================================================
// Types
// ============================================================================

export interface PortalAuthContextType {
  user: PortalUserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  permissions: Set<string>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  clearPasswordChangeRequired: () => void; // limpa o flag após troca obrigatória
}

// ============================================================================
// JWT decode helper (no external lib — JWT payload is base64url JSON)
// ============================================================================

function decodeJWT(token: string): Record<string, unknown> {
  try {
    const payload = token.split(".")[1];
    if (!payload) return {};
    // base64url → base64
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    // pad
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
}

function extractPermissions(token: string | null): Set<string> {
  if (!token) return new Set();
  const claims = decodeJWT(token) as { permissions?: string[] };
  return new Set(claims.permissions ?? []);
}

// ============================================================================
// Context Creation
// ============================================================================

const PortalAuthContext = createContext<PortalAuthContextType | undefined>(undefined);

// ============================================================================
// Provider Component
// ============================================================================

export const PortalAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<PortalUserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<Set<string>>(() =>
    extractPermissions(portalStorage.getAccessToken())
  );

  /**
   * Refresh permissions from current access token in storage.
   * Called after login / refresh / logout transitions so UI reflects
   * the JWT `permissions` claim without reloading the page.
   */
  const refreshPermissionsFromStorage = useCallback(() => {
    setPermissions(extractPermissions(portalStorage.getAccessToken()));
  }, []);

  /**
   * Check if token is expired
   * JWT tokens have exp claim, but we can decode and check
   */
  const isTokenExpired = useCallback((token: string): boolean => {
    try {
      // Decode JWT payload (base64)
      const payload = JSON.parse(atob(token.split(".")[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds

      // Check if token expires within next 5 minutes (300000 ms)
      return Date.now() >= exp - 300000;
    } catch {
      // If we can't decode, assume expired
      return true;
    }
  }, []);

  /**
   * Initialize authentication state on mount
   * Check stored tokens and refresh if needed
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        const accessToken = portalStorage.getAccessToken();
        const refreshToken = portalStorage.getRefreshToken();
        const storedUser = portalStorage.getUser();

        // No tokens stored
        if (!accessToken || !refreshToken || !storedUser) {
          setPermissions(new Set());
          setIsLoading(false);
          return;
        }

        // Token is expired or about to expire - refresh it
        if (isTokenExpired(accessToken)) {
          try {
            await portalAuthApi.refresh(refreshToken);
            // Refresh updates the access token in storage
            setUser(storedUser);
            refreshPermissionsFromStorage();
          } catch (refreshError) {
            // Refresh failed - clear everything
            console.error("Token refresh failed:", refreshError);
            portalStorage.clear();
            setUser(null);
            setPermissions(new Set());
          }
        } else {
          // Token is still valid
          setUser(storedUser);
          refreshPermissionsFromStorage();
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
        portalStorage.clear();
        setUser(null);
        setPermissions(new Set());
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [isTokenExpired, refreshPermissionsFromStorage]);

  /**
   * Login function
   * @param email - User email
   * @param password - User password
   */
  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const credentials: PortalLoginRequest = { email, password };
      const response = await portalAuthApi.login(credentials);

      // portalAuthApi.login already stores tokens and user.
      // Entrar é um dos dois gestos que trocam o dono do push do aparelho (o outro é ligar no cartão; decisão de 12/10/2026). A marca
      // vai junto com a sessão, antes do `setUser`: o `ReinscricaoDoPush` do portal monta no render seguinte e consulta a marca.
      marcarDonoDoPush("portal");
      setUser(response.user);
      refreshPermissionsFromStorage();
    } catch (err) {
      // Manutenção (PR 2, 28/09/2026, mesmo contrato de AuthContext.tsx): o
      // backend recusa POST /portal/auth/login com 503 + `{maintenance:
      // true, message}` quando a manutenção trava. `portalAuthApi.login` usa
      // axios puro — o erro chega como AxiosError, não como `Response` do
      // fetch. Sem este caso, caía no `getErrorMessage` genérico (o
      // `detail` que este 503 não tem) e a tela de login do portal não tinha
      // como distinguir de qualquer outro erro.
      if (
        axios.isAxiosError(err) &&
        err.response?.status === 503 &&
        (err.response.data as { maintenance?: boolean } | undefined)?.maintenance === true
      ) {
        const dadosErro = err.response.data as { message?: string };
        const erroDeManutencao = new Error(
          dadosErro.message || "O sistema está em manutenção. Tente novamente mais tarde."
        ) as Error & { manutencao?: boolean };
        erroDeManutencao.manutencao = true;
        setError(erroDeManutencao.message);
        throw erroDeManutencao;
      }

      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout function
   * Clears all portal authentication data and redirects to login
   */
  const logout = useCallback(() => {
    // `portalAuthApi.logout` limpa o armazenamento da sessão, e é o `portalStorage.clear` que larga a marca de dono do push (se era do
    // portal): o interno, ainda logado, pode adotar o push na próxima carga. Toda saída do portal passa por ele, não só esta.
    portalAuthApi.logout();
    setUser(null);
    setError(null);
    setPermissions(new Set());

    // Redirect to portal login page
    window.location.href = "/portal/login";
  }, []);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Limpa o flag de troca obrigatória após a troca de senha (estado + storage),
   * pra o ProtectedPortalRoute parar de redirecionar pra tela de troca.
   */
  const clearPasswordChangeRequired = useCallback(() => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, password_change_required: false };
      portalStorage.setUser(updated);
      return updated;
    });
  }, []);

  const value: PortalAuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    permissions,
    login,
    logout,
    clearError,
    clearPasswordChangeRequired,
  };

  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
};

// ============================================================================
// Custom Hook
// ============================================================================

/**
 * Hook to access portal authentication context
 * @throws Error if used outside PortalAuthProvider
 */
export const usePortalAuth = (): PortalAuthContextType => {
  const context = useContext(PortalAuthContext);

  if (!context) {
    throw new Error("usePortalAuth must be used within a PortalAuthProvider");
  }

  return context;
};

// ============================================================================
// Exports
// ============================================================================

export default PortalAuthContext;
